import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { FormBuilderState, initialState } from './form.state';
import { Form, FormStatus, FormTemplateType, QuestionView, QuestionType } from '../models';
import { FormApiService } from '../services/form-api.service';
import { FormElementApiService } from '../services/form-element-api.service';
import { ApplicationTemplateMapper, ExistingFormElementMapper } from '@formrails/shared/data-access';

/**
 * Check if a question is a structural element (Section, Title, or HelperText)
 */
function isStructuralElement(question: QuestionView): boolean {
  return (
    question.questionType === QuestionType.Section ||
    question.questionType === QuestionType.Title ||
    question.questionType === QuestionType.HelperText
  );
}

/**
 * Find the section ID for a question at a given index.
 * Looks backwards to find the most recent Section before this position.
 */
function findSectionId(questions: QuestionView[], index: number): string | undefined {
  for (let i = index - 1; i >= 0; i--) {
    if (questions[i].questionType === QuestionType.Section) {
      return questions[i].id;
    }
  }
  return undefined;
}

/**
 * Recalculate question numbers and section IDs for all questions.
 * - Each question after a Section is associated with that Section's ID.
 * - Questions before any Section have sectionId = undefined.
 * - Title and HelperText elements are associated with their parent section ID but get no number.
 * - Section elements themselves do not have a sectionId or number.
 */
function recalculateQuestionNumbers(questions: QuestionView[]): QuestionView[] {
  if (!questions || questions.length === 0) {
    return [];
  }

  // Sort questions by order FIRST so section association is computed against the visible order.
  const sortedQuestions = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  let questionCounter = 1;
  return sortedQuestions.map((q, index) => {
    if (q.questionType === QuestionType.Section) {
      return { ...q, order: index, questionNumber: undefined, sectionId: undefined };
    }

    if (q.questionType === QuestionType.Title || q.questionType === QuestionType.HelperText) {
      const sectionId = findSectionId(sortedQuestions, index);
      return { ...q, order: index, questionNumber: undefined, sectionId };
    }

    const sectionId = findSectionId(sortedQuestions, index);
    const updated = { ...q, order: index, questionNumber: questionCounter, sectionId };
    questionCounter++;
    return updated;
  });
}

/**
 * Feature-level signal store for the form builder. Replaces the classic NgRx
 * actions/reducer/effects/selectors trio with a single signal store: `withState`
 * holds the slice, `withComputed` mirrors the former selectors, and `withMethods`
 * replaces the reducer cases (synchronous `patchState`) and the effects (`rxMethod`).
 */
export const FormStore = signalStore(
  { providedIn: 'root' },
  withState<FormBuilderState>(initialState),

  withComputed(state => ({
    formTitle: computed(() => state.currentForm()?.title || ''),
    formDescription: computed(() => state.currentForm()?.description || ''),
    formVersion: computed(() => state.currentForm()?.version ?? 0),
    formStatus: computed(() => state.currentForm()?.status),
    showQuestionNumbers: computed(() => state.currentForm()?.showQuestionNumbers ?? true),
    questions: computed(() => state.currentForm()?.questions || []),
    formType: computed(() => state.currentForm()?.formType ?? null),
  })),

  withMethods(
    (
      store,
      formApi = inject(FormApiService),
      formElementApi = inject(FormElementApiService)
    ) => ({
      // --- Synchronous mutations (former reducer cases) ---

      initializeForm(formId: string): void {
        const newForm: Form = {
          id: formId,
          title: '',
          description: '',
          version: 0,
          status: FormStatus.Draft,
          showQuestionNumbers: true,
          questions: [],
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        patchState(store, { currentForm: newForm, error: null, isReadOnly: false });
      },

      updateFormTitle(title: string): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, { currentForm: { ...currentForm, title, updatedAt: new Date() } });
      },

      /** Record which backend owns this template (routes saves/loads). */
      updateFormType(formType: FormTemplateType | null): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, {
          currentForm: { ...currentForm, formType: formType ?? undefined, updatedAt: new Date() }
        });
      },

      updateFormDescription(description: string): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, { currentForm: { ...currentForm, description, updatedAt: new Date() } });
      },

      updateFormVersion(version: number): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, { currentForm: { ...currentForm, version, updatedAt: new Date() } });
      },

      /** Where a clone came from; cleared once the clone has been saved. */
      updateClonedFrom(clonedFromId: string | null): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, { currentForm: { ...currentForm, clonedFromId, updatedAt: new Date() } });
      },

      updateFormStatus(status: FormStatus): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, { currentForm: { ...currentForm, status, updatedAt: new Date() } });
      },

      toggleShowQuestionNumbers(showQuestionNumbers: boolean): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, {
          currentForm: { ...currentForm, showQuestionNumbers, updatedAt: new Date() },
        });
      },

      addQuestion(question: QuestionView): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        const finalQuestions = recalculateQuestionNumbers([...currentForm.questions, question]);
        patchState(store, {
          currentForm: { ...currentForm, questions: finalQuestions, updatedAt: new Date() },
        });
      },

      addQuestions(questions: QuestionView[]): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        const finalQuestions = recalculateQuestionNumbers([...currentForm.questions, ...questions]);
        patchState(store, {
          currentForm: { ...currentForm, questions: finalQuestions, updatedAt: new Date() },
        });
      },

      updateQuestion(question: QuestionView): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        const updatedQuestions = currentForm.questions.map(q =>
          q.id === question.id ? question : q
        );

        const oldQuestion = currentForm.questions.find(q => q.id === question.id);
        const typeChanged = !!oldQuestion && oldQuestion.questionType !== question.questionType;
        const wasUndefined = !oldQuestion?.questionType;
        const isNowDefined = !!question.questionType;
        const structuralChanged =
          !!oldQuestion && isStructuralElement(question) !== isStructuralElement(oldQuestion);
        const becameSection =
          question.questionType === QuestionType.Section &&
          oldQuestion?.questionType !== QuestionType.Section;
        const isTitleOrHelperText =
          question.questionType === QuestionType.Title ||
          question.questionType === QuestionType.HelperText ||
          oldQuestion?.questionType === QuestionType.Title ||
          oldQuestion?.questionType === QuestionType.HelperText;

        const needsRecalculation =
          (typeChanged && (structuralChanged || (wasUndefined && isNowDefined) || becameSection)) ||
          isTitleOrHelperText;

        const finalQuestions = needsRecalculation
          ? recalculateQuestionNumbers(updatedQuestions)
          : updatedQuestions;

        patchState(store, {
          currentForm: { ...currentForm, questions: finalQuestions, updatedAt: new Date() },
        });
      },

      deleteQuestion(questionId: string): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        const filtered = currentForm.questions.filter(q => q.id !== questionId);
        patchState(store, {
          currentForm: {
            ...currentForm,
            questions: recalculateQuestionNumbers(filtered),
            updatedAt: new Date(),
          },
        });
      },

      reorderQuestions(questions: QuestionView[]): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, {
          currentForm: {
            ...currentForm,
            questions: recalculateQuestionNumbers(questions),
            updatedAt: new Date(),
          },
        });
      },

      saveForm(status: FormStatus): void {
        const currentForm = store.currentForm();
        if (!currentForm) return;
        patchState(store, {
          currentForm: { ...currentForm, status, updatedAt: new Date() },
          loading: false,
        });
      },

      setReadOnly(isReadOnly: boolean): void {
        patchState(store, { isReadOnly });
      },

      // --- Asynchronous loaders (former effects) ---

      loadExistingFormElements: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, { loadingExistingElements: true, existingElementsError: null })
          ),
          switchMap(() =>
            formElementApi.getExistingFormElements().pipe(
              tapResponse({
                next: dtos =>
                  patchState(store, {
                    existingFormElements: ExistingFormElementMapper.toModelArray(dtos),
                    loadingExistingElements: false,
                    existingElementsError: null,
                  }),
                error: (error: { message?: string }) =>
                  patchState(store, {
                    loadingExistingElements: false,
                    existingElementsError:
                      error?.message || 'Failed to load existing form elements',
                  }),
              })
            )
          )
        )
      ),

      loadForms: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { loadingForms: true, formsError: null })),
          switchMap(() =>
            formApi.getForms().pipe(
              tapResponse({
                next: templates =>
                  patchState(store, { forms: templates, loadingForms: false, formsError: null }),
                error: (error: { message?: string; error?: { message?: string } }) =>
                  patchState(store, {
                    loadingForms: false,
                    formsError: error?.message || error?.error?.message || 'Failed to load forms',
                  }),
              })
            )
          )
        )
      ),

      getFormByTemplateId: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loadingFormById: true, formByIdError: null })),
          switchMap(templateId =>
            formApi.getFormByTemplateId(templateId).pipe(
              tapResponse({
                next: templateDto =>
                  patchState(store, {
                    currentForm: ApplicationTemplateMapper.toForm(templateDto),
                    loadingFormById: false,
                    formByIdError: null,
                  }),
                error: (error: { message?: string; error?: { message?: string } }) =>
                  patchState(store, {
                    loadingFormById: false,
                    formByIdError:
                      error?.message || error?.error?.message || 'Failed to load form template',
                  }),
              })
            )
          )
        )
      ),

      getRawFormByTemplateId: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { loadingFormById: true, formByIdError: null })),
          switchMap(templateId =>
            formApi.getRawFormByTemplateId(templateId).pipe(
              tapResponse({
                next: templateDto =>
                  patchState(store, {
                    currentForm: ApplicationTemplateMapper.toForm(templateDto),
                    loadingFormById: false,
                    formByIdError: null,
                  }),
                error: (error: { message?: string; error?: { message?: string } }) =>
                  patchState(store, {
                    loadingFormById: false,
                    formByIdError:
                      error?.message || error?.error?.message || 'Failed to load form template',
                  }),
              })
            )
          )
        )
      ),
    })
  )
);
